"use client";

import { useState, useCallback, type FC } from "react";
import type { WaitingForUserInput, UserInputType } from "@/lib/useEurekaRuntime";
import { ResourceFormRenderer, mcpSchemaToFormSchema, cleanFormValues } from "@/components/resource-form";
import type { ResourceSchema } from "@/components/resource-form";

interface UserInputPromptProps {
  waitingForUserInput: WaitingForUserInput;
  onSubmit: (response: string) => void;
}

/**
 * User Input Prompt Component
 * Renders different input types for agent ask_user requests:
 * - free_text: Text input field
 * - single_select: Radio button options
 * - multi_select: Checkbox options
 * - confirmation: Yes/No buttons
 */
export const UserInputPrompt: FC<UserInputPromptProps> = ({
  waitingForUserInput,
  onSubmit,
}) => {
  const { question, options, inputType, timeoutAt, formSchema, formValues, formAction, resourceType, resourceId } = waitingForUserInput;
  const [textValue, setTextValue] = useState("");
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [isFormLoading, setIsFormLoading] = useState(false);

  const handleSubmit = () => {
    let response: string;

    switch (inputType) {
      case "free_text":
        if (!textValue.trim()) return;
        response = textValue.trim();
        break;
      case "single_select":
        if (!selectedOption) return;
        response = selectedOption;
        break;
      case "multi_select":
        if (selectedOptions.size === 0) return;
        response = Array.from(selectedOptions).join(", ");
        break;
      case "confirmation":
        if (!selectedOption) return;
        response = selectedOption;
        break;
      default:
        response = textValue.trim();
    }

    onSubmit(response);
  };

  // Handle resource form submission
  const handleFormSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setIsFormLoading(true);
      try {
        const cleanedValues = cleanFormValues(values);
        const response = JSON.stringify({
          action: formAction,
          resource_type: resourceType,
          resource_id: resourceId,
          values: cleanedValues,
        });
        onSubmit(response);
      } finally {
        setIsFormLoading(false);
      }
    },
    [formAction, resourceType, resourceId, onSubmit]
  );

  // Handle form cancel
  const handleFormCancel = useCallback(() => {
    onSubmit(JSON.stringify({ action: "cancel" }));
  }, [onSubmit]);

  // Debug logging
  console.log('UserInputPrompt received:', { inputType, formSchema, formValues, formAction, resourceType, resourceId });

  // Render resource form if inputType is resource_form
  if (inputType === "resource_form" && formSchema) {
    console.log('Rendering ResourceFormRenderer with schema:', formSchema);
    const convertedSchema: ResourceSchema = mcpSchemaToFormSchema(formSchema as never);

    return (
      <div className="border-l-2 border-emerald-500 pl-3 py-2 my-2 bg-card/50 rounded-r">
        <div className="flex items-center gap-2 text-emerald-400 mb-2">
          <span className="font-semibold">
            {formAction === "create" ? "CREATE RESOURCE" : formAction === "edit" ? "EDIT RESOURCE" : "VIEW RESOURCE"}
          </span>
        </div>
        {question && (
          <div className="text-foreground mb-3 whitespace-pre-wrap">{question}</div>
        )}
        <ResourceFormRenderer
          schema={convertedSchema}
          initialValues={(formValues || {}) as Record<string, unknown>}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          mode={formAction === "view" ? "view" : formAction || "create"}
          isLoading={isFormLoading}
        />
      </div>
    );
  }

  const toggleMultiOption = (option: string) => {
    const newSet = new Set(selectedOptions);
    if (newSet.has(option)) {
      newSet.delete(option);
    } else {
      newSet.add(option);
    }
    setSelectedOptions(newSet);
  };

  // Calculate time remaining if timeout specified
  const timeRemaining = timeoutAt ? getTimeRemaining(timeoutAt) : null;

  return (
    <div className="border-l-2 border-amber-500 pl-3 py-2 my-2 bg-card/50 rounded-r">
      {/* Question header */}
      <div className="flex items-center gap-2 text-amber-400 mb-2">
        <span className="font-semibold">INPUT REQUIRED</span>
        {timeRemaining && (
          <>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground text-xs">
              expires in {timeRemaining}
            </span>
          </>
        )}
      </div>

      {/* Question text */}
      <div className="text-foreground mb-3 whitespace-pre-wrap">
        {question}
      </div>

      {/* Input area based on type */}
      <div className="mb-3">
        {inputType === "free_text" && (
          <FreeTextInput value={textValue} onChange={setTextValue} onSubmit={handleSubmit} />
        )}

        {inputType === "single_select" && options && (
          <SingleSelectInput
            options={options}
            selected={selectedOption}
            onSelect={setSelectedOption}
          />
        )}

        {inputType === "multi_select" && options && (
          <MultiSelectInput
            options={options}
            selected={selectedOptions}
            onToggle={toggleMultiOption}
          />
        )}

        {inputType === "confirmation" && (
          <ConfirmationInput selected={selectedOption} onSelect={setSelectedOption} />
        )}
      </div>

      {/* Submit button */}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit(inputType, textValue, selectedOption, selectedOptions)}
        className="px-3 py-1.5 bg-amber-600 text-white rounded text-sm font-medium
                   hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed
                   transition-colors"
      >
        Submit Response
      </button>
    </div>
  );
};

/**
 * Free text input with Enter key support
 */
const FreeTextInput: FC<{
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}> = ({ value, onChange, onSubmit }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === "Enter" && value.trim()) {
        onSubmit();
      }
    }}
    placeholder="Type your response..."
    className="w-full bg-muted text-foreground px-3 py-2 rounded border border-border
               focus:border-amber-500 focus:outline-none placeholder:text-muted-foreground"
    autoFocus
  />
);

/**
 * Single select (radio buttons)
 */
const SingleSelectInput: FC<{
  options: string[];
  selected: string | null;
  onSelect: (option: string) => void;
}> = ({ options, selected, onSelect }) => (
  <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
    {options.map((option) => (
      <label
        key={option}
        className="flex items-center gap-2 cursor-pointer group"
      >
        <input
          type="radio"
          name="single-select"
          checked={selected === option}
          onChange={() => onSelect(option)}
          className="w-4 h-4 text-amber-500 bg-muted border-border
                     focus:ring-amber-500 focus:ring-offset-background"
        />
        <span className="text-foreground group-hover:text-foreground">{option}</span>
      </label>
    ))}
  </div>
);

/**
 * Multi select (checkboxes)
 */
const MultiSelectInput: FC<{
  options: string[];
  selected: Set<string>;
  onToggle: (option: string) => void;
}> = ({ options, selected, onToggle }) => (
  <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
    {options.map((option) => (
      <label
        key={option}
        className="flex items-center gap-2 cursor-pointer group"
      >
        <input
          type="checkbox"
          checked={selected.has(option)}
          onChange={() => onToggle(option)}
          className="w-4 h-4 text-amber-500 bg-muted border-border rounded
                     focus:ring-amber-500 focus:ring-offset-background"
        />
        <span className="text-foreground group-hover:text-foreground">{option}</span>
      </label>
    ))}
  </div>
);

/**
 * Confirmation (Yes/No buttons)
 */
const ConfirmationInput: FC<{
  selected: string | null;
  onSelect: (option: string) => void;
}> = ({ selected, onSelect }) => (
  <div className="flex gap-2">
    <button
      onClick={() => onSelect("Yes")}
      className={`px-4 py-1.5 rounded text-sm font-medium transition-colors
        ${selected === "Yes"
          ? "bg-emerald-600 text-white"
          : "bg-muted text-foreground hover:bg-secondary"
        }`}
    >
      Yes
    </button>
    <button
      onClick={() => onSelect("No")}
      className={`px-4 py-1.5 rounded text-sm font-medium transition-colors
        ${selected === "No"
          ? "bg-red-600 text-white"
          : "bg-muted text-foreground hover:bg-secondary"
        }`}
    >
      No
    </button>
  </div>
);

/**
 * Check if form can be submitted based on input type
 */
function canSubmit(
  inputType: UserInputType,
  textValue: string,
  selectedOption: string | null,
  selectedOptions: Set<string>
): boolean {
  switch (inputType) {
    case "free_text":
      return textValue.trim().length > 0;
    case "single_select":
    case "confirmation":
      return selectedOption !== null;
    case "multi_select":
      return selectedOptions.size > 0;
    default:
      return false;
  }
}

/**
 * Calculate human-readable time remaining
 */
function getTimeRemaining(timeoutAt: string): string {
  const now = new Date();
  const timeout = new Date(timeoutAt);
  const diffMs = timeout.getTime() - now.getTime();

  if (diffMs <= 0) return "expired";

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s`;

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;

  const diffHour = Math.floor(diffMin / 60);
  return `${diffHour}h ${diffMin % 60}m`;
}
